import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Flag, ListChecks, MountainSnow, Route, Users } from "lucide-react";
import { fetchExplorerCollection, type ExplorerCollectionItem } from "../../api/social";
import { useAuth } from "../../hooks/useAuth";
import AppShell from "../../layouts/AppShell";
import { AtlasPanel } from "../../components/AtlasPanel";
import { ProgressRing } from "../../components/ProgressRing";
import { Badge } from "../../components/Badge";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { GuestTopBar } from "../../components/GuestTopBar";

const TYPE_ICON: Record<ExplorerCollectionItem["itemType"], React.ReactNode> = {
  peak: <MountainSnow size={18} />,
  landmark: <Flag size={18} />,
  trail: <Route size={18} />,
};

function itemMeta(item: ExplorerCollectionItem): string {
  if (item.itemType === "peak") return item.elevationM != null ? `${item.elevationM}m` : "Peak";
  if (item.itemType === "landmark") return item.category ? item.category.replace(/_/g, " ") : "Landmark";
  if (item.totalSections != null && item.totalSections > 0) {
    return `${item.completedSections ?? 0}/${item.totalSections} sections`;
  }
  return item.distanceKm != null ? `${item.distanceKm.toFixed(1)} km` : "Trail";
}

function ExplorerCollectionContent({
  username,
  data,
}: {
  username: string;
  data: { collection: { name: string; description: string | null }; items: ExplorerCollectionItem[] };
}) {
  const navigate = useNavigate();
  const { collection, items } = data;
  const completedCount = items.filter((i) => i.collected).length;
  const itemCount = items.length;

  return (
    <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
      <button
        type="button"
        onClick={() => void navigate({ to: "/explorers/$username", params: { username } })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> @{username}
      </button>

      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <ProgressRing value={completedCount} max={Math.max(itemCount, 1)} size={110} stroke={11} label="Collected" />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Custom list · @{username}</div>
            <h1 style={{ font: "var(--type-display)", fontSize: 36, color: "var(--text-primary)", margin: "0 0 8px", letterSpacing: "-.02em" }}>
              {collection.name}
            </h1>
            {collection.description && (
              <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 12px", maxWidth: 480 }}>
                {collection.description}
              </p>
            )}
            <Badge variant="gold" dot>{completedCount} / {itemCount}</Badge>
          </div>
        </div>
      </div>

      <AtlasPanel title={`Items (${itemCount})`} bodyStyle={{ padding: 0 }}>
        {itemCount === 0 ? (
          <div style={{ padding: 16 }}>
            <EmptyState icon={<ListChecks size={18} />} title="This list is empty" />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {items.map((item) => (
              <div
                key={`${item.itemType}-${item.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "12px 18px",
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "var(--radius-md)",
                      background: item.collected ? "var(--accent-soft)" : "var(--surface-sunken)",
                      border: "1px solid var(--border-default)",
                      color: item.collected ? "var(--gold-400)" : "var(--text-faint)",
                    }}
                  >
                    {TYPE_ICON[item.itemType]}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{item.name}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{itemMeta(item)}</div>
                  </div>
                </div>
                {item.collected ? (
                  <Badge variant="gold" dot>Collected</Badge>
                ) : (
                  <Badge variant="neutral">Not yet</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </AtlasPanel>
    </div>
  );
}

export default function ExplorerCollectionPage() {
  const params = useParams({ strict: false }) as { username?: string; id?: string };
  const username = params.username ?? "";
  const id = params.id ?? "";
  const { user: me, isLoading: meLoading } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["explorers", username, "collections", id],
    queryFn: () => fetchExplorerCollection(username, id),
    enabled: !!username && !!id,
    retry: false,
  });

  let content: React.ReactNode;

  if (isLoading || meLoading) {
    content = (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="180px" />
        <Skel height="220px" />
      </div>
    );
  } else if (isError || !data) {
    content = (
      <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<Users size={18} />} title="List not found" />
      </div>
    );
  } else {
    content = <ExplorerCollectionContent username={username} data={data} />;
  }

  if (me) {
    return <AppShell>{content}</AppShell>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <GuestTopBar />
      {content}
    </div>
  );
}
